import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState, useMemo } from 'react';
import useFetch from 'use-http';
import { X, FileText, Loader2 } from 'src/components/common/icons';
import { ICompany } from './Maintenance1099Modal';

// --- Type Definitions ---
export interface ILeaseTinArchiveDetail {
    lsNo: number;
    chkDate: string | null;
    amount: number | null;
    clNo: number | null;
    clName: string | null;
    transmitDate: string | null;
    correctAmt: number | null;
    correctDate: string | null;
    timestamp: string | null;
    snapshotTime: string | null;
    withAmount: number | null;
}

export interface ILeaseTinArchive {
    snapshotTime: string | null;
    changeReason: string | null;
    tinId: number;
    tin: string | null;
    tinType: string | null;
    taxYear: number | null;
    compId: number | null;
    amount: number | null;
    name1: string | null;
    addr1: string | null;
    cstz: string | null;
    modBy: string | null;
    modDate: string | null;
    transmitDate: string | null;
    correctAmount: number | null;
    correctDate: string | null;
    correctTransDate: string | null;
    mailDate: string | null;
    withAmount: number | null;
    tinVerified: number | null;
    tinVerDate: string | null;
    details: ILeaseTinArchiveDetail[];
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    tinId: number | null;
}

const TinHistoryModal: React.FC<IProps> = ({ isOpen, onClose, tinId }) => {
    const { get, response, loading } = useFetch();
    const [historyData, setHistoryData] = useState<ILeaseTinArchive[]>([]);
    const [companies, setCompanies] = useState<ICompany[]>([]);

    useEffect(() => {
        if (isOpen && tinId) {
            loadData();
            loadCompanies();
        } else {
            setHistoryData([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, tinId]);

    const loadData = async () => {
        const historyRes = await get(`/v1.0/leases/tins/${tinId}/history`);
        if (response.ok && Array.isArray(historyRes)) {
            setHistoryData(historyRes as ILeaseTinArchive[]);
        }
    };

    const loadCompanies = async () => {
        const comps = await get('/v1.0/leases/master/companies');
        if (response.ok && Array.isArray(comps)) {
            setCompanies(comps as ICompany[]);
        }
    };

    // Flatten all details using .reduce instead of .flatMap to support ES5/ES2015 targets
    const allHistoricalDetails = useMemo(() => {
        if (!historyData || historyData.length === 0) {
            return [];
        }
        return historyData.reduce((acc: ILeaseTinArchiveDetail[], record) => {
            return acc.concat(record.details || []);
        }, []);
    }, [historyData]);

    const formatMoney = (val?: number | null) =>
        val !== null && val !== undefined ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '';
    const formatDate = (val?: string | null) => (val ? new Date(val).toLocaleDateString() : '');

    if (!isOpen) {
        return null;
    }

    // Use the most recent header record for the top form
    const currentRecord = historyData[0];

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>
                        <FileText style={styles.headerIcon} />
                        TIN History Archive
                    </h2>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.modalBody}>
                    {loading ? (
                        <div style={styles.loadingState}>
                            <Loader2 className="w-8 h-8 animate-spin" style={styles.loaderIcon} />
                            <span style={styles.loadingText}>Loading History...</span>
                        </div>
                    ) : !currentRecord ? (
                        <div style={styles.emptyState}>
                            <FileText style={styles.emptyIcon} />
                            <span style={styles.emptyText}>No historical records found.</span>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: '2rem' }}>
                            {/* Top Form Section (Fixed at top) */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '3rem', flexShrink: 0 }}>
                                {/* Left Form */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '120px 150px 70px 100px 50px 100px',
                                            gap: '0.5rem',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <span style={localStyles.label}>Tax ID No.</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, color: '#4F46E5', fontWeight: 'bold' }}
                                            readOnly={true}
                                            value={currentRecord.tin || ''}
                                        />
                                        <span style={{ ...localStyles.label, textAlign: 'right' }}>Tax Year</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={currentRecord.taxYear || ''} />
                                        <span style={{ ...localStyles.label, textAlign: 'right' }}>Type</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={currentRecord.tinType || ''} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Company</span>
                                        <select style={localStyles.input} disabled={true} value={currentRecord.compId?.toString() || ''}>
                                            <option value="">Select...</option>
                                            {companies.map((c) => (
                                                <option key={c.id} value={c.id.toString()}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Name</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={currentRecord.name1 || ''} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Address</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={currentRecord.addr1 || ''} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>City, State Zip</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={currentRecord.cstz || ''} />
                                    </div>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '120px 100px 150px 200px',
                                            gap: '0.5rem',
                                            alignItems: 'center',
                                            marginTop: '0.5rem',
                                        }}
                                    >
                                        <span style={localStyles.label}>Verified</span>
                                        <div style={localStyles.greenBadge}>{currentRecord.tinVerified ? 'YES' : 'NO'}</div>
                                        <div style={localStyles.greenBadge}>PREVIOUS YEAR</div>
                                        <div style={localStyles.greenText}>{formatDate(currentRecord.tinVerDate)}</div>
                                    </div>
                                </div>

                                {/* Right Form */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Tax ID</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, textAlign: 'center' }}
                                            readOnly={true}
                                            value={currentRecord.tinId || ''}
                                        />
                                    </div>
                                    <div style={{ height: '0.5rem' }} />
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Total Amount</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, textAlign: 'right' }}
                                            readOnly={true}
                                            value={formatMoney(currentRecord.amount)}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Total With. Amount</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, textAlign: 'right' }}
                                            readOnly={true}
                                            value={formatMoney(currentRecord.withAmount)}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Mail Date</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={formatDate(currentRecord.mailDate)} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Transmit Date</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={formatDate(currentRecord.transmitDate)} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Correction Amt.</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, textAlign: 'right' }}
                                            readOnly={true}
                                            value={formatMoney(currentRecord.correctAmount)}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Correction Date</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={formatDate(currentRecord.correctDate)} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Corr. Trans Date</span>
                                        <LeaseRecordInput
                                            style={localStyles.input}
                                            readOnly={true}
                                            value={formatDate(currentRecord.correctTransDate)}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Modified By</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, textTransform: 'uppercase' }}
                                            readOnly={true}
                                            value={currentRecord.modBy || ''}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Modified at</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={formatDate(currentRecord.modDate)} />
                                    </div>
                                </div>
                            </div>

                            {/* Details Grid Section (Scrollable) */}
                            <div style={{ ...styles.tableWrapper, flex: 1, minHeight: 0 }}>
                                <table style={styles.tableBase}>
                                    <thead style={styles.tableHead}>
                                        <tr>
                                            <th style={styles.tableHeaderCell}>Lease No.</th>
                                            <th style={styles.tableHeaderCell}>Check Date</th>
                                            <th style={{ ...styles.tableHeaderCell, textAlign: 'right' }}>Amount</th>
                                            <th style={{ ...styles.tableHeaderCell, textAlign: 'right' }}>Cl. No.</th>
                                            <th style={styles.tableHeaderCell}>Client Name</th>
                                            <th style={styles.tableHeaderCell}>Trans. Date</th>
                                            <th style={{ ...styles.tableHeaderCell, textAlign: 'right' }}>Corr. Amount</th>
                                            <th style={styles.tableHeaderCell}>Corr. Date</th>
                                            <th style={styles.tableHeaderCell}>Snapshot Time</th>
                                        </tr>
                                    </thead>
                                    <tbody style={styles.tableBody}>
                                        {allHistoricalDetails.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} style={styles.noResults}>
                                                    No historical details available
                                                </td>
                                            </tr>
                                        ) : (
                                            allHistoricalDetails.map((row: ILeaseTinArchiveDetail, i: number) => (
                                                <tr key={i} style={styles.tableRow}>
                                                    <td style={styles.tableDataCell}>
                                                        <button
                                                            style={styles.checkNoButton}
                                                            onDoubleClick={() => window.open(`/leases/${row.lsNo}`, '_blank')}
                                                            title="Double click to open Lease in new tab"
                                                        >
                                                            {row.lsNo}
                                                        </button>
                                                    </td>
                                                    <td style={styles.tableDataCell}>{formatDate(row.chkDate)}</td>
                                                    <td style={{ ...styles.tableDataCell, ...styles.amountCell, textAlign: 'right' }}>
                                                        {formatMoney(row.amount)}
                                                    </td>
                                                    <td style={{ ...styles.tableDataCell, textAlign: 'right' }}>{row.clNo}</td>
                                                    <td style={styles.tableDataCell}>{row.clName}</td>
                                                    <td style={styles.tableDataCell}>{formatDate(row.transmitDate)}</td>
                                                    <td style={{ ...styles.tableDataCell, ...styles.amountCell, textAlign: 'right' }}>
                                                        {formatMoney(row.correctAmt)}
                                                    </td>
                                                    <td style={styles.tableDataCell}>{formatDate(row.correctDate)}</td>
                                                    <td style={styles.tableDataCell}>{formatDate(row.snapshotTime)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <div style={styles.modalFooter}>
                    <button onClick={onClose} style={styles.buttonPrimary}>
                        Close History
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 60,
        padding: '1rem',
        backdropFilter: 'blur(2px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
        width: '1250px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: { fontSize: '1.5rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', margin: 0 },
    headerIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem', color: '#4F46E5' },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' },
    modalFooter: { padding: '1rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' },
    buttonPrimary: {
        padding: '0.6rem 1.5rem',
        border: 'none',
        borderRadius: '0.5rem',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.15s ease-in-out',
    },
    loadingState: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '12.5rem', color: '#4F46E5' },
    loaderIcon: { width: '2rem', height: '2rem', marginRight: '0.5rem', animation: 'spin 1s linear infinite' },
    loadingText: { fontSize: '1.125rem', fontWeight: 500 },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '12.5rem',
        color: '#6B7280',
    },
    emptyIcon: { width: '2.5rem', height: '2.5rem', marginBottom: '0.5rem' },
    emptyText: { fontSize: '1.125rem', fontWeight: 500 },
    tableWrapper: { overflowY: 'auto', borderRadius: '0.5rem', border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' },
    tableBase: { minWidth: '100%', borderCollapse: 'collapse' },
    tableHead: { backgroundColor: '#F9FAFB', position: 'sticky', top: 0, zIndex: 1 },
    tableHeaderCell: {
        padding: '0.75rem 1rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #E5E7EB',
    },
    tableBody: { backgroundColor: '#FFFFFF' },
    tableRow: { transition: 'background-color 0.1s ease', borderBottom: '1px solid #F3F4F6' },
    tableDataCell: { padding: '0.75rem 1rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#111827' },
    checkNoButton: {
        fontWeight: 600,
        color: '#4F46E5',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'underline',
        padding: 0,
        fontSize: '0.875rem',
    },
    amountCell: { fontWeight: 600 },
    noResults: { padding: '1.5rem', textAlign: 'center', color: '#6B7280', fontSize: '0.875rem' },
};

const localStyles: { [key: string]: React.CSSProperties } = {
    label: { fontSize: '0.875rem', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' },
    input: {
        padding: '0.5rem 0.75rem',
        border: '1px solid #D1D5DB',
        borderRadius: '0.375rem',
        fontSize: '0.875rem',
        color: '#111827',
        backgroundColor: '#F9FAFB',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
    },
    greenBadge: {
        backgroundColor: '#10B981',
        color: 'white',
        padding: '0.25rem 0.5rem',
        borderRadius: '0.25rem',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    greenText: {
        color: '#10B981',
        fontSize: '0.875rem',
        fontWeight: 'bold',
    },
};

export default TinHistoryModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/TinHistoryModal.tsx